/* FD rewards + announcement controller. */
(function () {
  'use strict';

  if (window.fdCartRewardsController) window.fdCartRewardsController.destroy();

  const cfg = window.fdCartTierConfig;
  if (!cfg) return;

  if (cfg.offerMode === 'hoodie_gifts') {
    initHoodieGiftMode(cfg);
    return;
  }

  const offerMode = cfg.offerMode === 'evergreen' ? 'evergreen' : 'bmsm';
  const announcementMode =
    cfg.announcementMode === 'static'
      ? 'static'
      : cfg.announcementMode === 'off'
      ? 'off'
      : 'rewards';

  const tiers = (cfg.tiers || []).map(t => ({
    amount: Number(t.amount),
    label: String(t.label || ''),
    code: String(t.code || '').trim(),
    virtual: Boolean(t.virtual)
  }));

  const autoCodes = offerMode === 'bmsm' && Boolean(cfg.autoCodes);
  const autoGift = offerMode === 'bmsm' && Boolean(cfg.autoGift);
  const giftVariantId = Number(cfg.giftVariantId || 0);

  const valid = tiers.length > 0 && tiers.every((tier, index) =>
    Number.isFinite(tier.amount) &&
    tier.amount >= 0 &&
    (!index || tier.amount > tiers[index - 1].amount)
  );

  const root = window.Shopify?.routes?.root || '/';
  const tag = '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M13 2h9v9L11 22 2 13 13 2Zm5 3a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Z"/></svg>';
  const check = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m4 12 5 5L20 6" fill="none" stroke="currentColor" stroke-width="4"/></svg>';

  let mounted = [];
  let timer;
  let busy = null;
  let pending = false;
  let disposed = false;
  let internalBuild = false;
  let lastError = '';
  let allowSubmit = false;
  let observer;
  let buildVersion = 0;
  let ignoreMutationsUntil = 0;
  const failures = new Set();
  const listeners = [];

  function listen(el, name, fn, opts) {
    el.addEventListener(name, fn, opts);
    listeners.push(() => el.removeEventListener(name, fn, opts));
  }

  function money(cents) {
    return window.theme?.Currency
      ? theme.Currency.formatMoney(cents, theme.settings.moneyFormat)
      : new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD'
        }).format(cents / 100);
  }

  function eligible(cart) {
    return (cart.items || []).reduce((sum, item) => {
      const gift =
        item.properties?._free_tier ||
        item.properties?._fd_bmsm_gift ||
        item.final_line_price === 0;

      return sum + (gift ? 0 : Number(item.original_line_price || 0));
    }, 0);
  }

  function targetIndex(total, count) {
    if (!count) return -1;

    let index = -1;
    tiers.forEach((tier, i) => {
      if (total >= tier.amount) index = i;
    });

    return index;
  }

  function progress(total, count) {
    if (!count || tiers.length < 2) return 0;

    const index = targetIndex(total, count);
    if (index < 0) return 0;
    if (index >= tiers.length - 1) return 1;

    const segment =
      (total - tiers[index].amount) /
      (tiers[index + 1].amount - tiers[index].amount);

    return (index + segment) / (tiers.length - 1);
  }

  function setContainerVisibility(container, visible) {
    container.hidden = !visible;
    container.style.display = visible ? '' : 'none';
  }

  function renderStaticAnnouncement(container) {
    const promo = cfg.staticPromo || {};
    const text = String(promo.text || '').trim();
    const url = String(promo.url || '').trim();

    container.classList.add('fd-static-announcement-mounted');
    container.classList.remove('fd-rewards-mounted');

    if (!text) {
      setContainerVisibility(container, false);
      return;
    }

    setContainerVisibility(container, true);

    if (url) {
      container.innerHTML =
        '<a class="fd-static-announcement" href="' +
        url.replace(/"/g, '&quot;') +
        '"' +
        (promo.newTab ? ' target="_blank" rel="noopener"' : '') +
        '><span>' +
        text.replace(/</g, '&lt;').replace(/>/g, '&gt;') +
        '</span></a>';
    } else {
      container.innerHTML =
        '<div class="fd-static-announcement"><span>' +
        text.replace(/</g, '&lt;').replace(/>/g, '&gt;') +
        '</span></div>';
    }
  }

  function mount() {
    mounted = [];

    document.querySelectorAll('[data-cart-goal]').forEach(container => {
      const banner = container.classList.contains('cart-goal-banner-container');

      container.classList.remove(
        'fd-rewards-mounted',
        'fd-rewards-mounted--evergreen',
        'fd-static-announcement-mounted'
      );

      // Top announcement is controlled separately from the cart drawer.
      if (banner) {
        if (announcementMode === 'off') {
          setContainerVisibility(container, false);
          return;
        }

        if (announcementMode === 'static') {
          renderStaticAnnouncement(container);
          return;
        }
      }

      // Drawer rewards still use the selected cart offer.
      if (!cfg.enabled || (banner && announcementMode !== 'rewards')) {
        setContainerVisibility(container, false);
        return;
      }

      container.classList.add('fd-rewards-mounted');
      container.classList.toggle(
        'fd-rewards-mounted--evergreen',
        offerMode === 'evergreen'
      );

      setContainerVisibility(container, true);

      container.innerHTML =
        '<div class="fd-rewards fd-rewards--' +
        offerMode +
        '" style="--fd-tier-count:' +
        tiers.length +
        ';--fd-tier-edge:' +
        50 / tiers.length +
        '%">' +
        '<div class="fd-rewards-message">' +
        tag +
        '<span></span></div>' +
        '<div class="fd-rewards-track">' +
        '<div class="fd-rewards-rail">' +
        '<span class="fd-rewards-fill"></span>' +
        '</div>' +
        '<ol class="fd-rewards-list"></ol>' +
        '</div>' +
        '<p class="fd-rewards-status" role="status" aria-live="polite"></p>' +
        '</div>';

      const list = container.querySelector('ol');

      tiers.forEach(tier => {
        const li = document.createElement('li');

        if (tier.virtual) li.classList.add('fd-rewards-spacer');

        li.innerHTML =
          '<span class="fd-rewards-dot">' +
          tag +
          '</span>' +
          '<span class="fd-rewards-label"></span>';

        const label = li.querySelector('.fd-rewards-label');
        const parts = tier.label.split(/\s*\+\s*/);

        label.textContent = parts[0] + (parts.length > 1 ? ' +' : '');

        if (parts.length > 1) {
          const sub = document.createElement('span');
          sub.textContent = parts.slice(1).join(' + ');
          label.append(sub);
        }

        list.append(li);
      });

      mounted.push(container);
    });
  }

  function status(text) {
    mounted.forEach(container => {
      const el = container.querySelector('.fd-rewards-status');
      if (el) el.textContent = '';
    });

    if (text) console.warn('FD rewards:', text);
  }

  function normalized(code) {
    return String(code).toUpperCase();
  }

  function updateFooterDiscountBadge(cart) {
    const discountLabel = document.querySelector(
      '#CartDrawer .fd-total-discounts > div:first-child'
    );

    if (!discountLabel) return;

    let badge = discountLabel.querySelector('.fd-applied-tier-badge');

    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'fd-applied-tier-badge';
      discountLabel.append(badge);
    }

    const appliedCodes = Array.isArray(cart.discount_codes)
      ? cart.discount_codes
          .filter(discount => discount.applicable)
          .map(discount => normalized(discount.code))
      : [];

    let appliedTier =
      offerMode === 'bmsm'
        ? tiers.find(
            tier =>
              tier.code &&
              appliedCodes.includes(normalized(tier.code))
          )
        : null;

    if (
      !appliedTier &&
      offerMode === 'bmsm' &&
      Number(cart.total_discount || 0) > 0
    ) {
      const reachedIndex = targetIndex(
        eligible(cart),
        Number(cart.item_count || 0)
      );

      if (reachedIndex >= 0) appliedTier = tiers[reachedIndex];
    }

    if (!appliedTier) {
      badge.textContent = '';
      badge.hidden = true;
      return;
    }

    const percentage = appliedTier.label.match(/\d+%\s*OFF/i);

    badge.textContent = percentage
      ? percentage[0].toUpperCase()
      : appliedTier.label;

    badge.hidden = false;
  }

  function render(cart) {
    const itemCount = Number(cart.item_count || 0);
    const total = eligible(cart);
    const index = targetIndex(total, itemCount);
    const next = tiers[index + 1];
    const firstReward = tiers.find(
      tier => !tier.virtual && tier.label
    );

    updateFooterDiscountBadge(cart);

    mounted.forEach(container => {
      const banner = container.classList.contains('cart-goal-banner-container');

      const shouldShow =
        cfg.enabled &&
        (!banner || announcementMode === 'rewards');

      setContainerVisibility(container, shouldShow);

      if (!shouldShow) return;

      const fill = container.querySelector('.fd-rewards-fill');
      if (fill) {
        fill.style.width =
          Math.max(0, Math.min(1, progress(total, itemCount))) * 100 + '%';
      }

      container.querySelectorAll('li').forEach((li, i) => {
        const unlocked = i <= index;

        li.classList.toggle('is-unlocked', unlocked);
        li.querySelector('.fd-rewards-dot').innerHTML = unlocked
          ? check
          : tag;

        li.setAttribute(
          'aria-label',
          tiers[i].label +
            (unlocked
              ? ' — threshold reached'
              : ' — upcoming')
        );
      });

      const message = container.querySelector('.fd-rewards-message span');
      if (!message) return;

      message.replaceChildren();

      if (!itemCount) {
        if (offerMode === 'evergreen' && firstReward) {
          message.append('Add ');

          const amount = document.createElement('u');
          amount.textContent = money(firstReward.amount);

          message.append(amount, ' to unlock ' + firstReward.label);
        } else {
          message.textContent = 'Add an item to unlock ' + tiers[0].label;
        }
      } else if (next) {
        message.append('Add ');

        const amount = document.createElement('u');
        amount.textContent = money(Math.max(0, next.amount - total));

        message.append(amount, ' to unlock ' + next.label);
      } else {
        message.textContent = cfg.success || "You've unlocked all rewards!";
      }
    });

    status(lastError);
  }

  async function request(path, body) {
    const response = await fetch(root + path, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...(body
        ? {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
              Accept: 'application/json'
            },
            body: JSON.stringify(body)
          }
        : {})
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(
        result.description ||
          result.message ||
          'Cart request failed'
      );
    }

    return result;
  }

  async function syncBmsmGift(cart) {
    if (!autoGift || !giftVariantId || tiers.length < 3) {
      return { cart: cart, changed: false };
    }

    const threshold = tiers[2].amount;
    const reached = eligible(cart) >= threshold;

    const giftLine = (cart.items || []).find(
      item =>
        Number(item.variant_id) === giftVariantId &&
        item.properties?._fd_bmsm_gift
    );

    if (reached && !giftLine) {
      await request('cart/add.js', {
        items: [
          {
            id: giftVariantId,
            quantity: 1,
            properties: { _fd_bmsm_gift: '1' }
          }
        ]
      });

      return {
        cart: await request('cart.js'),
        changed: true
      };
    }

    if (!reached && giftLine) {
      const updated = await request('cart/change.js', {
        id: giftLine.key,
        quantity: 0
      });

      return { cart: updated, changed: true };
    }

    if (
      reached &&
      giftLine &&
      Number(giftLine.quantity) !== 1
    ) {
      const updated = await request('cart/change.js', {
        id: giftLine.key,
        quantity: 1
      });

      return { cart: updated, changed: true };
    }

    return { cart: cart, changed: false };
  }

  function rebuild() {
    buildVersion += 1;
    ignoreMutationsUntil = Date.now() + 650;
    internalBuild = true;

    document.dispatchEvent(new CustomEvent('cart:build'));

    internalBuild = false;
  }

  async function run() {
    if (disposed || !cfg.enabled) return;

    if (!valid) {
      lastError =
        'Check that all reward thresholds are entered in increasing order.';
      status(lastError);
      return;
    }

    let cart = await request('cart.js');

    if (cart.currency && cart.currency !== 'USD') {
      lastError =
        'This offer is configured for USD. Currency-specific thresholds are not configured.';
      status(lastError);
      return;
    }

    const giftSync = await syncBmsmGift(cart);
    cart = giftSync.cart;

    render(cart);

    if (!autoCodes) {
      if (giftSync.changed) rebuild();
      return;
    }

    if (
      tiers.some(tier => !tier.code) ||
      new Set(
        tiers.map(tier => normalized(tier.code))
      ).size !== tiers.length
    ) {
      lastError =
        'Enter distinct Shopify discount codes for every Buy More, Save More tier.';
      status(lastError);
      return;
    }

    if (!Array.isArray(cart.discount_codes)) {
      lastError =
        'Unable to verify existing cart codes. Automatic switching paused.';
      status(lastError);
      return;
    }

    const index = targetIndex(
      eligible(cart),
      cart.item_count
    );

    const desired =
      index >= 0 ? tiers[index].code : '';

    const managed = new Set(
      tiers.map(tier => normalized(tier.code))
    );

    const current = cart.discount_codes.filter(discount =>
      managed.has(normalized(discount.code))
    );

    const external = cart.discount_codes
      .filter(
        discount =>
          !managed.has(normalized(discount.code))
      )
      .map(discount => discount.code);

    const externalApplied = cart.discount_codes
      .filter(
        discount =>
          !managed.has(normalized(discount.code)) &&
          discount.applicable
      )
      .map(discount => discount.code);

    const desiredAlreadyApplied =
      current.length === 1 &&
      normalized(current[0].code) === normalized(desired) &&
      (
        current[0].applicable ||
        Number(cart.total_discount || 0) > 0
      );

    if (
      (!desired && current.length === 0) ||
      desiredAlreadyApplied
    ) {
      lastError = '';
      render(cart);

      if (giftSync.changed) rebuild();
      return;
    }

    const key = JSON.stringify([
      desired,
      cart.items.map(item => [
        item.variant_id,
        item.quantity,
        item.original_line_price
      ]),
      external
    ]);

    if (failures.has(key)) {
      lastError =
        'Tier discount could not be confirmed. Check code eligibility and combinations.';
      status(lastError);
      return;
    }

    const codes = [
      ...external,
      ...(desired ? [desired] : [])
    ];

    cart = await request('cart/update.js', {
      discount: codes.join(',')
    });

    const acceptedByCode =
      !desired ||
      (cart.discount_codes || []).some(
        discount =>
          normalized(discount.code) === normalized(desired) &&
          discount.applicable
      );

    const acceptedBySavings =
      Boolean(desired) &&
      Number(cart.total_discount || 0) > 0;

    const accepted = acceptedByCode || acceptedBySavings;

    const lostOther = externalApplied.some(
      code =>
        !(cart.discount_codes || []).some(
          discount =>
            normalized(discount.code) === normalized(code) &&
            discount.applicable
        )
    );

    if (!accepted || lostOther) {
      failures.add(key);

      lastError = lostOther
        ? 'A discount combination was rejected. Check compatibility with KITE.'
        : 'Tier code was not accepted. Check its Shopify settings.';
    } else {
      lastError = '';
    }

    render(cart);
    rebuild();
  }

  function sync() {
    if (busy) {
      pending = true;
      return busy;
    }

    busy = (async () => {
      let rounds = 0;

      do {
        pending = false;

        try {
          await run();
        } catch (error) {
          lastError = '';
          status('');
          console.warn('FD rewards:', error.message);
        }
      } while (
        pending &&
        !disposed &&
        ++rounds < 3
      );
    })().finally(() => {
      busy = null;
    });

    return busy;
  }

  function schedule() {
    if (disposed || internalBuild) return;

    clearTimeout(timer);
    timer = setTimeout(sync, 120);
  }

  async function changeDrawerQuantity(input, quantity) {
    const wrapper = input.closest('.js-qty__wrapper');
    const key = input.dataset.id;

    if (
      !key ||
      wrapper?.classList.contains('is-loading')
    ) {
      return;
    }

    const nextQuantity = Math.max(
      0,
      Number.parseInt(quantity, 10) || 0
    );

    input.value = nextQuantity;
    wrapper?.classList.add('is-loading');

    try {
      clearTimeout(timer);

      if (busy) await busy;

      let cart;

      try {
        cart = await request('cart/change.js', {
          id: key,
          quantity: nextQuantity
        });
      } catch (firstError) {
        const updates = {};
        updates[key] = nextQuantity;

        cart = await request('cart/update.js', {
          updates: updates
        });
      }

      lastError = '';
      const versionBeforeSync = buildVersion;

      await sync();

      if (buildVersion === versionBeforeSync) {
        rebuild();
      }
    } catch (error) {
      console.warn('FD cart quantity:', error.message);
      rebuild();
    } finally {
      wrapper?.classList.remove('is-loading');
    }
  }

  function drawerQuantityClick(event) {
    const button = event.target.closest(
      '#CartDrawer .js-qty__adjust'
    );

    if (!button) return;

    const wrapper = button.closest('.js-qty__wrapper');
    const input = wrapper?.querySelector('.js-qty__num');

    if (!input) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const current =
      Number.parseInt(input.value, 10) || 0;

    const next = button.classList.contains(
      'js-qty__adjust--plus'
    )
      ? current + 1
      : current - 1;

    changeDrawerQuantity(input, next);
  }

  function drawerQuantityInput(event) {
    const input = event.target.closest(
      '#CartDrawer .js-qty__num'
    );

    if (!input) return;

    event.stopImmediatePropagation();
    changeDrawerQuantity(input, input.value);
  }

  async function checkout(event) {
    if (
      !autoCodes ||
      !cfg.enabled ||
      allowSubmit
    ) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    clearTimeout(timer);

    const form = event.target;
    const submitter = event.submitter;

    await sync();

    if (disposed) return;

    if (lastError) {
      console.warn('FD rewards checkout:', lastError);
      lastError = '';
      status('');
    }

    allowSubmit = true;

    try {
      form.requestSubmit(submitter || undefined);
    } finally {
      allowSubmit = false;
    }
  }

  function init() {
    mount();

    listen(document, 'click', drawerQuantityClick, true);
    listen(document, 'change', drawerQuantityInput, true);

    if (!cfg.enabled) return;

    [
      'cart:updated',
      'ajaxProduct:added'
    ].forEach(name =>
      listen(document, name, schedule)
    );

    listen(document, 'cart:build', () => {
      if (Date.now() >= ignoreMutationsUntil) {
        schedule();
      }
    });

    const drawer = document.getElementById('CartDrawer');

    if (drawer) {
      observer = new MutationObserver(schedule);

      observer.observe(drawer, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    const products =
      drawer?.querySelector('[data-products]');

    if (products) {
      const productsObserver =
        new MutationObserver(() => {
          if (Date.now() >= ignoreMutationsUntil) {
            schedule();
          }
        });

      productsObserver.observe(products, {
        childList: true
      });

      listeners.push(() =>
        productsObserver.disconnect()
      );
    }

    document
      .querySelectorAll(
        '#CartDrawerForm, form[data-location="cart-page"]'
      )
      .forEach(form =>
        listen(form, 'submit', checkout, true)
      );

    sync();
  }

  window.fdCartRewardsController = {
    destroy() {
      disposed = true;
      clearTimeout(timer);
      observer?.disconnect();
      listeners.forEach(fn => fn());
    }
  };

  if (document.readyState === 'loading') {
    listen(
      document,
      'DOMContentLoaded',
      init,
      { once: true }
    );
  } else {
    init();
  }

  function initHoodieGiftMode(config) {
    const root = window.Shopify?.routes?.root || '/';
    const giftCfg = config.hoodieGifts || {};
    const shippingThreshold = Number(giftCfg.shippingThreshold || 5500);
    const gift1Threshold = Number(giftCfg.gift1Threshold || 9900);
    const gift2Threshold = Number(giftCfg.gift2Threshold || 12400);
    const collections = giftCfg.collections || {};
    const listeners = [];
    let disposed = false;
    let busy = false;
    let timer = null;
    let observer = null;

    const esc = value => String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

    function listen(el, name, fn, opts) {
      if (!el) return;
      el.addEventListener(name, fn, opts);
      listeners.push(() => el.removeEventListener(name, fn, opts));
    }

    function money(cents) {
      return window.theme?.Currency
        ? theme.Currency.formatMoney(cents, theme.settings.moneyFormat)
        : new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
          }).format(Number(cents || 0) / 100);
    }

    async function request(path, body) {
      const response = await fetch(root + path, {
        credentials: 'same-origin',
        cache: 'no-store',
        ...(body ? {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/json'
          },
          body: JSON.stringify(body)
        } : {})
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result.description ||
          result.message ||
          'Cart request failed'
        );
      }

      return result;
    }

    function eligibleSubtotal(cart) {
      /*
        Reward thresholds must follow what the shopper is actually paying
        after Shopify automatic product discounts, not the merchandise's
        original compare/subtotal value.

        Example:
        2 hoodies = $129.98 regular, HOODIE2 discounts them to $99.
        The shopper should unlock the $99 gift, but NOT the $124 gift.
      */
      return (cart.items || []).reduce((sum, item) => {
        const isGift = Boolean(
          item.properties?._fd_free_gift ||
          item.properties?._fd_gift_tier
        );

        if (isGift) return sum;

        const paidLinePrice =
          item.final_line_price != null
            ? Number(item.final_line_price)
            : item.line_price != null
              ? Number(item.line_price)
              : Number(item.original_line_price || 0);

        return sum + paidLinePrice;
      }, 0);
    }

    function giftLine(cart, tier) {
      return (cart.items || []).find(item =>
        String(item.properties?._fd_gift_tier || '') === String(tier)
      );
    }

    function milestoneData(total) {
      return [
        {
          key: 'shipping',
          amount: shippingThreshold,
          label: 'FREE SHIPPING',
          unlocked: total >= shippingThreshold
        },
        {
          key: 'gift1',
          amount: gift1Threshold,
          label: String(giftCfg.gift1Heading || 'FREE GIFT').toUpperCase(),
          unlocked: total >= gift1Threshold
        },
        {
          key: 'gift2',
          amount: gift2Threshold,
          label: String(giftCfg.gift2Heading || '2ND FREE GIFT').toUpperCase(),
          unlocked: total >= gift2Threshold
        }
      ];
    }

    function progressPercent(total) {
      /*
        The progress rail visually runs from the center of the $55 milestone
        to the center of the $124 milestone, with the $99 milestone at 50%.
        So the fill must be interpolated between milestones rather than using
        total / finalThreshold.
      */
      if (total <= shippingThreshold) return 0;
      if (total >= gift2Threshold) return 100;

      if (total < gift1Threshold) {
        const span = Math.max(1, gift1Threshold - shippingThreshold);
        return Math.max(
          0,
          Math.min(50, ((total - shippingThreshold) / span) * 50)
        );
      }

      const span = Math.max(1, gift2Threshold - gift1Threshold);
      return Math.max(
        50,
        Math.min(
          100,
          50 + ((total - gift1Threshold) / span) * 50
        )
      );
    }

    function messageForTotal(total) {
      if (total < shippingThreshold) {
        return 'Add ' + money(shippingThreshold - total) + ' to unlock Free Shipping';
      }

      if (total < gift1Threshold) {
        return 'Add ' + money(gift1Threshold - total) + ' to unlock ' +
          String(giftCfg.gift1Heading || 'your first free gift');
      }

      if (total < gift2Threshold) {
        return 'Add ' + money(gift2Threshold - total) + ' to unlock ' +
          String(giftCfg.gift2Heading || 'your second free gift');
      }

      return String(config.success || 'Free Shipping + both gifts unlocked!');
    }

    function renderStaticAnnouncement(container) {
      const promo = config.staticPromo || {};
      const text = String(promo.text || '').trim();
      const url = String(promo.url || '').trim();

      container.classList.add('fd-static-announcement-mounted');
      container.classList.remove('fd-rewards-mounted');

      if (!text) {
        container.hidden = true;
        container.style.display = 'none';
        return;
      }

      container.hidden = false;
      container.style.display = '';

      container.innerHTML = url
        ? '<a class="fd-static-announcement" href="' + esc(url) + '"' +
          (promo.newTab ? ' target="_blank" rel="noopener"' : '') +
          '><span>' + esc(text) + '</span></a>'
        : '<div class="fd-static-announcement"><span>' +
          esc(text) +
          '</span></div>';
    }

    function mountAnnouncement() {
      document
        .querySelectorAll('.cart-goal-banner-container[data-cart-goal]')
        .forEach(container => {
          container.classList.remove(
            'fd-rewards-mounted',
            'fd-rewards-mounted--evergreen',
            'fd-static-announcement-mounted'
          );

          if (config.announcementMode === 'off') {
            container.hidden = true;
            container.style.display = 'none';
            return;
          }

          if (config.announcementMode === 'static') {
            renderStaticAnnouncement(container);
            return;
          }

          container.hidden = false;
          container.style.display = '';
          container.classList.add('fd-rewards-mounted');
        });
    }

    function ensureGiftHost() {
      const drawer = document.getElementById('CartDrawer');
      const products = drawer?.querySelector('[data-products]');
      if (!drawer || !products) return null;

      let host = drawer.querySelector('[data-fd-gift-rewards]');

      if (!host) {
        host = document.createElement('div');
        host.className = 'fd-gift-rewards';
        host.setAttribute('data-fd-gift-rewards', '');
        products.insertAdjacentElement('afterend', host);
      }

      return host;
    }

    function renderProgress(cart) {
      const total = eligibleSubtotal(cart);
      const milestones = milestoneData(total);

      document
        .querySelectorAll('#CartDrawer [data-cart-goal]')
        .forEach(container => {
          container.hidden = false;
          container.style.display = '';
          container.classList.add('fd-hoodie-progress-mounted');

          container.innerHTML =
            '<div class="fd-hg-progress">' +
              '<div class="fd-hg-progress__message">' +
                '<span>' + esc(messageForTotal(total)) + '</span>' +
              '</div>' +
              '<div class="fd-hg-progress__track">' +
                '<div class="fd-hg-progress__rail">' +
                  '<span class="fd-hg-progress__fill" style="width:' +
                    progressPercent(total) + '%"></span>' +
                '</div>' +
                '<ol class="fd-hg-progress__list">' +
                  milestones.map(m =>
                    '<li class="' + (m.unlocked ? 'is-unlocked' : '') + '">' +
                      '<span class="fd-hg-progress__dot">' +
                        (m.unlocked
                          ? '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4 10-10" fill="none" stroke="currentColor" stroke-width="3"/></svg>'
                          : '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 10V8a5 5 0 0 1 10 0v2M5 10h14v11H5z" fill="none" stroke="currentColor" stroke-width="2"/></svg>') +
                      '</span>' +
                      '<strong>' + esc(m.label) + '</strong>' +
                      '<small>' + esc(money(m.amount)) + '</small>' +
                    '</li>'
                  ).join('') +
                '</ol>' +
              '</div>' +
            '</div>';
        });

      if (config.announcementMode === 'rewards') {
        document
          .querySelectorAll('.cart-goal-banner-container[data-cart-goal]')
          .forEach(container => {
            container.hidden = false;
            container.style.display = '';
            container.classList.add('fd-hoodie-progress-mounted');
            container.innerHTML =
              '<div class="fd-hg-banner-message">' +
                esc(messageForTotal(total)) +
              '</div>';
          });
      }
    }

    function productCard(product, tier, unlocked, selectedVariantId) {
      const available = (product.variants || []).some(v => v.available);
      const selected = (product.variants || []).some(
        v => Number(v.id) === Number(selectedVariantId)
      );

      return (
        '<button type="button" class="fd-gift-card' +
          (unlocked ? ' is-unlocked' : ' is-locked') +
          (selected ? ' is-selected' : '') +
          '" data-fd-gift-card data-tier="' + tier + '"' +
          ' data-product-handle="' + esc(product.handle) + '"' +
          (!unlocked || !available ? ' disabled' : '') +
        '>' +
          '<span class="fd-gift-card__image-wrap">' +
            (product.image
              ? '<img class="fd-gift-card__image" src="' + esc(product.image) +
                '" alt="' + esc(product.title) + '" loading="lazy">'
              : '<span class="fd-gift-card__image fd-gift-card__image--empty"></span>') +
            (!unlocked
              ? '<span class="fd-gift-card__lock"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 10V8a5 5 0 0 1 10 0v2M5 10h14v11H5z" fill="none" stroke="currentColor" stroke-width="2"/></svg></span>'
              : '') +
            (selected
              ? '<span class="fd-gift-card__selected">SELECTED</span>'
              : '') +
          '</span>' +
          '<span class="fd-gift-card__title">' + esc(product.title) + '</span>' +
          '<span class="fd-gift-card__price">' +
            '<s>' + esc(money(product.price)) + '</s> <strong>FREE</strong>' +
          '</span>' +
        '</button>'
      );
    }

    function sectionHtml(tier, total, cart) {
      const threshold = tier === 1 ? gift1Threshold : gift2Threshold;
      const heading = tier === 1
        ? String(giftCfg.gift1Heading || 'Free Gift')
        : String(giftCfg.gift2Heading || '2nd Free Gift');

      const unlocked = total >= threshold;
      const products = collections[String(tier)] || [];
      const selectedLine = giftLine(cart, tier);
      const selectedVariantId = selectedLine?.variant_id || 0;
      const remaining = Math.max(0, threshold - total);
      const lockedTemplate = String(giftCfg.lockedText || 'Add [amount] to unlock');
      const status = unlocked
        ? String(giftCfg.unlockedText || 'FREE GIFT UNLOCKED — CHOOSE ONE')
        : lockedTemplate.replace('[amount]', money(remaining));

      return (
        '<section class="fd-gift-tier ' + (unlocked ? 'is-unlocked' : 'is-locked') +
          '" data-fd-gift-tier="' + tier + '">' +
          '<div class="fd-gift-tier__head">' +
            '<div>' +
              '<span class="fd-gift-tier__eyebrow">' +
                esc(heading.toUpperCase()) + ' • ' + esc(money(threshold)) +
              '</span>' +
              '<h3>' + esc(status) + '</h3>' +
            '</div>' +
            (selectedLine
              ? '<span class="fd-gift-tier__chosen">GIFT CHOSEN ✓</span>'
              : '') +
          '</div>' +
          (products.length
            ? '<div class="fd-gift-tier__products">' +
                products.map(product =>
                  productCard(product, tier, unlocked, selectedVariantId)
                ).join('') +
              '</div>'
            : '<div class="fd-gift-tier__empty">Choose a collection in the Cart Tiers customizer.</div>') +
        '</section>'
      );
    }

    function renderGiftSections(cart) {
      const host = ensureGiftHost();
      if (!host) return;

      const total = eligibleSubtotal(cart);
      host.innerHTML =
        sectionHtml(1, total, cart) +
        sectionHtml(2, total, cart);
    }

    function findProduct(tier, handle) {
      return (collections[String(tier)] || []).find(
        p => String(p.handle) === String(handle)
      );
    }

    function ensureModal() {
      let modal = document.querySelector('[data-fd-gift-modal]');
      if (modal) return modal;

      modal = document.createElement('div');
      modal.className = 'fd-gift-modal drawer';
      modal.setAttribute('data-fd-gift-modal', '');
      modal.setAttribute('aria-hidden', 'true');

      modal.innerHTML =
        '<div class="fd-gift-modal__backdrop" data-fd-gift-modal-close></div>' +
        '<div class="fd-gift-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="fdGiftModalTitle">' +
          '<button type="button" class="fd-gift-modal__close" data-fd-gift-modal-close aria-label="Close">×</button>' +
          '<div class="fd-gift-modal__content"></div>' +
        '</div>';

      document.body.appendChild(modal);
      return modal;
    }

    function closeModal() {
      const modal = document.querySelector('[data-fd-gift-modal]');
      if (!modal) return;
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('fd-gift-modal-open');
    }

    function openModal(tier, product) {
      const modal = ensureModal();
      const content = modal.querySelector('.fd-gift-modal__content');
      const availableVariants = (product.variants || []).filter(v => v.available);
      const first = availableVariants[0];

      if (!first) return;

      content.innerHTML =
        '<div class="fd-gift-modal__image-wrap">' +
          (first.image || product.image
            ? '<img data-fd-modal-image src="' + esc(first.image || product.image) +
              '" alt="' + esc(product.title) + '">'
            : '') +
        '</div>' +
        '<div class="fd-gift-modal__body">' +
          '<span class="fd-gift-modal__eyebrow">YOUR FREE GIFT</span>' +
          '<h2 id="fdGiftModalTitle">' + esc(product.title) + '</h2>' +
          '<div class="fd-gift-modal__price"><s>' +
            esc(money(first.price || product.price)) +
            '</s> <strong>FREE</strong></div>' +
          (availableVariants.length > 1
            ? '<label class="fd-gift-modal__label" for="fdGiftVariantSelect">Choose option</label>' +
              '<select id="fdGiftVariantSelect" class="fd-gift-modal__select" data-fd-gift-variant>' +
                availableVariants.map(v =>
                  '<option value="' + v.id + '" data-image="' + esc(v.image || product.image) +
                    '" data-price="' + Number(v.price || product.price) + '">' +
                    esc(v.title) +
                  '</option>'
                ).join('') +
              '</select>'
            : '<input type="hidden" data-fd-gift-variant value="' + first.id + '">') +
          '<button type="button" class="fd-gift-modal__add" data-fd-gift-add' +
            ' data-tier="' + tier + '">' +
            esc(giftCfg.modalButtonText || 'ADD FREE GIFT') +
          '</button>' +
        '</div>';

      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('fd-gift-modal-open');
    }

    async function removeLineByKey(key) {
      if (!key) return null;
      return request('cart/change.js', {
        id: key,
        quantity: 0
      });
    }

    async function addGift(tier, variantId) {
      if (busy) return;
      busy = true;

      try {
        let cart = await request('cart.js');
        const total = eligibleSubtotal(cart);
        const threshold = tier === 1 ? gift1Threshold : gift2Threshold;

        if (total < threshold) {
          closeModal();
          await sync();
          return;
        }

        const existing = giftLine(cart, tier);
        if (existing) {
          cart = await removeLineByKey(existing.key);
        }

        await request('cart/add.js', {
          items: [{
            id: Number(variantId),
            quantity: 1,
            properties: {
              _fd_free_gift: '1',
              _fd_gift_tier: String(tier),
              _fd_gift_threshold: String(threshold)
            }
          }]
        });

        closeModal();
        document.dispatchEvent(new CustomEvent('cart:build'));
        setTimeout(sync, 160);
      } catch (error) {
        console.warn('FD free gift:', error.message);
      } finally {
        busy = false;
      }
    }

    async function enforceGifts(cart) {
      const total = eligibleSubtotal(cart);
      const removals = [];

      (cart.items || []).forEach(item => {
        const tier = Number(item.properties?._fd_gift_tier || 0);
        if (!tier) return;

        const threshold = tier === 1 ? gift1Threshold : gift2Threshold;

        if (total < threshold) {
          removals.push(item.key);
          return;
        }

        if (Number(item.quantity) !== 1) {
          removals.push({ key: item.key, replace: item.variant_id, tier });
        }
      });

      if (!removals.length) return cart;

      for (const item of removals) {
        if (typeof item === 'string') {
          await removeLineByKey(item);
        } else {
          await removeLineByKey(item.key);
          await request('cart/add.js', {
            items: [{
              id: Number(item.replace),
              quantity: 1,
              properties: {
                _fd_free_gift: '1',
                _fd_gift_tier: String(item.tier),
                _fd_gift_threshold: String(
                  item.tier === 1 ? gift1Threshold : gift2Threshold
                )
              }
            }]
          });
        }
      }

      return request('cart.js');
    }

    async function sync() {
      if (disposed || busy || !config.enabled) return;

      busy = true;

      try {
        let cart = await request('cart.js');

        if (cart.currency && cart.currency !== 'USD') {
          console.warn('FD free gifts: thresholds are configured for USD.');
          return;
        }

        const beforeCount = Number(cart.item_count || 0);
        cart = await enforceGifts(cart);

        renderProgress(cart);
        renderGiftSections(cart);

        if (Number(cart.item_count || 0) !== beforeCount) {
          document.dispatchEvent(new CustomEvent('cart:build'));
        }
      } catch (error) {
        console.warn('FD hoodie gifts:', error.message);
      } finally {
        busy = false;
      }
    }

    function schedule() {
      clearTimeout(timer);
      timer = setTimeout(sync, 140);
    }

    function onClick(event) {
      const close = event.target.closest('[data-fd-gift-modal-close]');
      if (close) {
        event.preventDefault();
        closeModal();
        return;
      }

      const card = event.target.closest('[data-fd-gift-card]');
      if (card) {
        event.preventDefault();

        const tier = Number(card.dataset.tier || 0);
        const product = findProduct(tier, card.dataset.productHandle);

        if (product && !card.disabled) {
          openModal(tier, product);
        }

        return;
      }

      const add = event.target.closest('[data-fd-gift-add]');
      if (add) {
        event.preventDefault();

        const modal = add.closest('[data-fd-gift-modal]');
        const variant = modal?.querySelector('[data-fd-gift-variant]');
        const variantId = Number(variant?.value || 0);
        const tier = Number(add.dataset.tier || 0);

        if (variantId && tier) addGift(tier, variantId);
      }
    }

    function onVariantChange(event) {
      const select = event.target.closest('select[data-fd-gift-variant]');
      if (!select) return;

      const option = select.options[select.selectedIndex];
      const modal = select.closest('[data-fd-gift-modal]');
      const image = modal?.querySelector('[data-fd-modal-image]');
      const price = Number(option?.dataset.price || 0);

      if (image && option?.dataset.image) {
        image.src = option.dataset.image;
      }

      const priceWrap = modal?.querySelector('.fd-gift-modal__price s');
      if (priceWrap && price) {
        priceWrap.textContent = money(price);
      }
    }

    function init() {
      mountAnnouncement();
      ensureGiftHost();

      listen(document, 'click', onClick, true);
      listen(document, 'change', onVariantChange, true);

      ['cart:updated', 'ajaxProduct:added', 'cart:build'].forEach(name =>
        listen(document, name, schedule)
      );

      const drawer = document.getElementById('CartDrawer');

      if (drawer) {
        observer = new MutationObserver(() => {
          if (drawer.classList.contains('drawer--is-open')) schedule();
        });

        observer.observe(drawer, {
          attributes: true,
          attributeFilter: ['class']
        });
      }

      sync();
    }

    window.fdCartRewardsController = {
      destroy() {
        disposed = true;
        clearTimeout(timer);
        observer?.disconnect();
        listeners.forEach(fn => fn());
        closeModal();
      }
    };

    if (document.readyState === 'loading') {
      listen(document, 'DOMContentLoaded', init, { once: true });
    } else {
      init();
    }
  }

})();